import React, { useState } from "react";
import { AlertTriangle, Sparkles, CheckCircle2, Lock, X } from "lucide-react";
import { formatLLMTokenRates } from "./LLMModelsConfig";

export const LLMTestConfirmModal = ({
  open,
  provider,
  model,
  onClose,
  onConfirm,
  loading = false,
}) => {
  const [authorized, setAuthorized] = useState(false);

  if (!open || !provider) return null;

  const isRateVerified = Boolean(model?.is_rate_verified);
  const inputCost = model?.cost_per_m_input_usd;
  const outputCost = model?.cost_per_m_output_usd;

  const numInput = Number(inputCost);
  const numOutput = Number(outputCost);

  const hasCostData =
    isRateVerified &&
    inputCost !== null &&
    inputCost !== undefined &&
    outputCost !== null &&
    outputCost !== undefined &&
    !isNaN(numInput) &&
    !isNaN(numOutput) &&
    numInput >= 0 &&
    numOutput >= 0;

  const estimatedCostUsd = hasCostData
    ? ((numInput / 1_000_000) * 5 + (numOutput / 1_000_000) * 1).toFixed(7)
    : null;

  const handleConfirm = () => {
    if (!authorized) return;
    onConfirm({
      test_type: "inference",
      model_id: model?.id,
      user_confirmed_cost: true,
    });
  };

  return (
    <div
      data-testid="llm-test-confirm-modal-overlay"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4"
    >
      <div
        data-testid="llm-test-confirm-modal"
        className="w-full max-w-lg rounded-2xl border border-[#2a2a2a] bg-[#141414] shadow-2xl p-6 text-left"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-[#262626] pb-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-amber-500/30 bg-amber-500/10 text-amber-400">
              <Sparkles size={20} />
            </div>
            <div>
              <h3 className="font-display text-base font-semibold text-[#ededed]">
                Autorizar Prueba de Inferencia
              </h3>
              <p className="text-xs text-[#8a8a8a]">
                Creative Director · {provider.label}
              </p>
            </div>
          </div>
          <button
            type="button"
            data-testid="llm-test-modal-close"
            disabled={loading}
            onClick={onClose}
            className="rounded-lg p-1.5 text-[#8a8a8a] hover:bg-[#222] hover:text-[#ededed] transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Cuerpo */}
        <div className="mt-4 flex flex-col gap-4">
          <div className="rounded-xl border border-[#262626] bg-[#181818] p-3 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-[#a3a3a3]">Modelo objetivo:</span>
              <span className="font-mono font-semibold text-[#f5b544]">
                {model?.label || model?.id || "Modelo predeterminado"}
              </span>
            </div>
            <div className="mt-2 flex items-center justify-between border-t border-[#222] pt-2">
              <span className="text-[#a3a3a3]">Tipo de carga:</span>
              <span className="text-[#ededed]">Ping sintético (5 tokens entrada, 1 token salida)</span>
            </div>
            <div className="mt-2 flex items-center justify-between border-t border-[#222] pt-2">
              <span className="text-[#a3a3a3]">Coste estimado:</span>
              {hasCostData && estimatedCostUsd !== null ? (
                <div className="text-right">
                  <span className="font-mono text-emerald-400 font-medium block">
                    ~${estimatedCostUsd} USD (Tarifa verificada)
                  </span>
                  <span className="font-mono text-[10px] text-[#888] block mt-0.5">
                    {formatLLMTokenRates(numInput, numOutput)}
                  </span>
                </div>
              ) : (
                <span className="inline-flex items-center gap-1 font-mono text-amber-400/90">
                  <AlertTriangle size={12} /> Coste desconocido
                </span>
              )}
            </div>
          </div>

          {!hasCostData && (
            <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-3 text-[11px] text-amber-300/90 leading-relaxed">
              ⚠️ <strong>Tarifa no verificada:</strong> Este modelo o endpoint no cuenta con tarifas contractualmente auditadas. El consumo exacto dependerá de su plan con el proveedor.
            </div>
          )}

          <p className="text-xs text-[#8a8a8a] leading-relaxed">
            Por política de seguridad de VEYRA, ninguna prueba con posible coste se ejecuta sin el consentimiento explícito del Propietario.
          </p>

          {/* Checkbox de autorización explícita */}
          <label
            data-testid="llm-test-authorize-label"
            className="flex items-start gap-3 rounded-xl border border-[#262626] bg-[#161616] p-3 cursor-pointer hover:border-[#383838] transition-colors"
          >
            <input
              type="checkbox"
              data-testid="llm-test-authorize-checkbox"
              checked={authorized}
              onChange={(e) => setAuthorized(e.target.checked)}
              disabled={loading}
              className="mt-0.5 h-4 w-4 rounded border-[#383838] bg-[#222] text-[#f5b544] focus:ring-0"
            />
            <span className="text-xs text-[#ededed] font-medium leading-relaxed">
              Autorizo expresamente a VEYRA a realizar esta llamada de inferencia sintética hacia el proveedor {provider.label}.
            </span>
          </label>
        </div>

        {/* Footer con acciones */}
        <div className="mt-6 flex items-center justify-end gap-2 border-t border-[#262626] pt-4">
          <button
            type="button"
            data-testid="llm-test-modal-cancel-btn"
            disabled={loading}
            onClick={onClose}
            className="v-btn-ghost v-btn-sm"
          >
            Cancelar
          </button>
          <button
            type="button"
            data-testid="llm-test-modal-confirm-btn"
            disabled={!authorized || loading}
            onClick={handleConfirm}
            className="v-btn-primary v-btn-sm inline-flex items-center gap-2"
          >
            {loading ? (
              <span>Ejecutando ping…</span>
            ) : (
              <>
                <Sparkles size={14} />
                <span>Confirmar y Probar Inferencia</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LLMTestConfirmModal;
